import { useState, useRef } from 'react';
import { NavLink, Link, useMatch } from 'react-router-dom';
import { motion, AnimatePresence, type Variants } from 'framer-motion';
import {
  LogOut, Settings,
  ChevronLeft, ChevronRight,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { usePreferencesStore } from '../../store/preferencesStore';
import { cn } from '../../utils/cn';
import {
  SvgIcon, ICON_HOME, ICON_FILES, ICON_AI,
  AI_TOOLS_ITEMS, PRICING_ITEM,
  type NavItem,
} from './navConfig';

// ── Style constants ────────────────────────────────────────────────────────────

const CARD_BG = cn(
  'bg-[#16171e]',
  'border border-white/[0.07]',
  'shadow-[0_0_0_1px_rgba(0,0,0,0.4),0_24px_48px_-8px_rgba(0,0,0,0.7)]',
  'ring-1 ring-inset ring-white/[0.04]',
);

// Fixed-width icon column. In the collapsed rail (64px card, nav px-2) a `w-12`
// column centres every icon on the card centre-line; in the expanded card the
// same column keeps every icon pinned at the exact same x — so the icon never
// moves and the label simply grows out to its right.
const ICON_COL = 'flex w-12 flex-shrink-0 items-center justify-center';

// ── Animation timeline ─────────────────────────────────────────────────────────
// One state (`expanded` | `collapsed`) is set on <motion.aside> and propagates to
// every nested motion child, so the whole thing is a single reversible timeline.

const EASE_OUT: [number, number, number, number] = [0.22, 1, 0.36, 1];
const EASE_IN:  [number, number, number, number] = [0.4, 0, 1, 1];

const WIDTH_SPRING = { type: 'spring' as const, stiffness: 240, damping: 30, mass: 1 };

// Only the always-visible parent rows (Home, AI, Admin, ...) animate: icons
// stay pinned while their labels grow out of them. Group bodies (the sub-item
// lists) are not shown in both states — they only ever appear inline while
// expanded — so they show/hide instantly with no animation of their own,
// keeping the animated surface limited to parent rows.

// The card owns width. On expand it opens up front so labels have room to grow;
// on collapse it waits (width.delay) for the labels to retract first.
const asideVariants: Variants = {
  collapsed: { width: 64,  transition: { width: { ...WIDTH_SPRING, delay: 0.2 } } },
  expanded:  { width: 264, transition: { width: WIDTH_SPRING, delayChildren: 0.04 } },
};

// Nav column + footer: gently cascade the Phase-1 labels/titles. (The group bodies
// are plain CSS-driven divs now, so only the labels flow through this stagger.)
const listVariants: Variants = {
  collapsed: { transition: { staggerChildren: 0.02, staggerDirection: -1 } },
  expanded:  { transition: { staggerChildren: 0.025, delayChildren: 0.04 } },
};

// Phase 1 — a label grows out of its pinned icon (leaf rows, group titles, brand).
const labelVariants: Variants = {
  collapsed: { width: 0,      opacity: 0, transition: { width: { duration: 0.24, ease: EASE_IN, delay: 0.08 }, opacity: { duration: 0.16, delay: 0.08 } } },
  expanded:  { width: 'auto', opacity: 1, transition: { width: { duration: 0.3, ease: EASE_OUT }, opacity: { duration: 0.24, ease: EASE_OUT } } },
};

// Phase 1 — profile text (its width is handled by flexbox, so only fade it in).
const fadeVariants: Variants = {
  collapsed: { opacity: 0, transition: { duration: 0.14 } },
  expanded:  { opacity: 1, transition: { duration: 0.24, ease: EASE_OUT } },
};

// ── Rows (shared by collapsed + expanded — one persistent tree) ─────────────────

/** Leaf nav row: a pinned icon + a label that grows out of it. */
function NavRow({
  to, icon, label, active, isLink,
}: {
  to: string; icon: React.ReactNode; label: string; active?: boolean; isLink?: boolean;
}) {
  const base = 'flex items-center rounded-xl py-2.5 pr-3 text-sm font-medium transition-colors';
  const activeCls = 'bg-brand-600/90 text-white shadow-sm';
  const idleCls   = 'text-zinc-400 hover:bg-white/[0.06] hover:text-zinc-100';

  const body = (
    <>
      <span className={ICON_COL}>{icon}</span>
      <motion.span variants={labelVariants} className="block shrink-0 overflow-hidden whitespace-nowrap">
        {label}
      </motion.span>
    </>
  );

  return isLink ? (
    <Link to={to} className={cn(base, active ? activeCls : idleCls)}>{body}</Link>
  ) : (
    <NavLink to={to} className={({ isActive }) => cn(base, isActive ? activeCls : idleCls)}>{body}</NavLink>
  );
}

/**
 * A group: an anchor row (pinned icon + growing uppercase title, animated like any
 * other parent row) plus a body. While the sidebar itself is collapsed, hovering the
 * anchor opens the flyout dropdown (unchanged). While the sidebar is expanded, the
 * anchor becomes a click-to-toggle accordion trigger — the body defaults to closed
 * and only opens once the user clicks it, so expanding the sidebar alone never
 * reveals more rows than the collapsed rail does. No animation on the body itself —
 * it shows/hides instantly.
 */
function GroupBlock({
  icon, title, active, sidebarExpanded, open, onToggle, onHoverOpen, onHoverClose, children,
}: {
  icon: React.ReactNode; title: string; active?: boolean; sidebarExpanded: boolean;
  open: boolean; onToggle: () => void;
  onHoverOpen?: (e: React.MouseEvent<HTMLDivElement>) => void;
  onHoverClose?: () => void;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div
        onMouseEnter={!sidebarExpanded ? onHoverOpen : undefined}
        onMouseLeave={!sidebarExpanded ? onHoverClose : undefined}
        onClick={sidebarExpanded ? onToggle : undefined}
        className={cn(
          'flex items-center rounded-xl py-2.5 pr-3 text-sm font-medium transition-colors cursor-pointer',
          active ? 'bg-brand-600/90 text-white shadow-sm' : 'text-zinc-400 hover:bg-white/[0.06] hover:text-zinc-100',
        )}
      >
        <span className={ICON_COL}>{icon}</span>
        <motion.span
          variants={labelVariants}
          className="block shrink-0 overflow-hidden whitespace-nowrap"
        >
          {title}
        </motion.span>
        {sidebarExpanded && (
          <ChevronRight
            className={cn(
              'ml-auto h-3.5 w-3.5 shrink-0 transition-transform duration-200',
              active ? 'text-white/70' : 'text-zinc-600',
              open && 'rotate-90',
            )}
          />
        )}
      </div>
      {sidebarExpanded && open && (
        <div className="ml-6 mt-0.5 space-y-0.5 border-l border-white/[0.08] py-0.5 pl-3">
          {children}
        </div>
      )}
    </div>
  );
}

/**
 * A sub-item inside a group body — only ever rendered while expanded, no animation.
 * Deliberately smaller/muted and indented (via the group's connector line) so it
 * reads as nested under its parent, not as a duplicate top-level row.
 */
function SubItemRow({ item }: { item: NavItem }) {
  return (
    <NavLink
      to={item.to}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-2.5 rounded-lg py-1.5 pr-3 text-[13px] font-normal whitespace-nowrap',
          isActive
            ? 'bg-brand-600/90 text-white shadow-sm'
            : 'text-zinc-500 hover:bg-white/[0.06] hover:text-zinc-200',
        )
      }
    >
      <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center [&>svg]:h-4 [&>svg]:w-4">{item.icon}</span>
      <span className="truncate">{item.label}</span>
    </NavLink>
  );
}

// ── Dropdown link (collapsed hover flyouts) ─────────────────────────────────────

function DropdownLink({ item, onClick }: { item: NavItem; onClick?: () => void }) {
  return (
    <NavLink
      to={item.to}
      onClick={onClick}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors',
          isActive
            ? 'text-white bg-white/[0.07]'
            : 'text-zinc-300 hover:bg-white/[0.06] hover:text-white'
        )
      }
    >
      {item.icon}
      <span>{item.label}</span>
    </NavLink>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function Sidebar() {
  const {
    sidebarExpanded, toggleSidebarExpanded, sidebarOpenOnHover,
  } = usePreferencesStore();

  // Ephemeral hover-expand (only when "open on hover" is enabled). Distinct from
  // the persisted `sidebarExpanded` (the pinned state).
  const [isHovered, setIsHovered] = useState(false);
  const isExpanded = sidebarExpanded || (sidebarOpenOnHover && isHovered);

  const [showToggle, setShowToggle]           = useState(false);
  const [activeDropdown, setActiveDropdown]   = useState<string | null>(null);
  // Per-group accordion state (AI/Admin) — only used while the sidebar itself
  // is expanded; defaults to closed so expanding the sidebar alone doesn't
  // reveal more rows than the collapsed rail does.
  const [openGroups, setOpenGroups]           = useState<Record<string, boolean>>({});
  const toggleGroup = (id: string) => setOpenGroups((g) => ({ ...g, [id]: !g[id] }));
  const [dropdownY, setDropdownY]             = useState(0);

  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapperRef    = useRef<HTMLDivElement>(null);

  const { user, isSuperAdmin, logout } = useAuth();

  // Active-section detection for group anchors.
  // IMPORTANT: every useMatch must be called unconditionally — never inside ||/&& short-circuits
  const _matchAiTools  = !!useMatch({ path: '/ai-tools/*', end: false });
  const _matchJournals = !!useMatch({ path: '/journals/*', end: false });
  const onAiRoute    = _matchAiTools || _matchJournals;
  const onSettingsRoute = !!useMatch('/settings');
  const onPricingRoute = !!useMatch('/pricing');

  const handleLogout = async () => {
    await logout();
  };

  if (!user) return null;

  // Dropdown helpers
  function openDropdown(e: React.MouseEvent<HTMLDivElement>, id: string) {
    // When "open on hover" is enabled, hovering already expands the whole
    // sidebar — the collapsed flyout dropdowns would be redundant/glitchy.
    if (sidebarOpenOnHover) return;
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    const wrapperTop = wrapperRef.current?.getBoundingClientRect().top ?? 0;
    const iconTop    = e.currentTarget.getBoundingClientRect().top;
    setDropdownY(iconTop - wrapperTop);
    setActiveDropdown(id);
  }
  function scheduleClose() {
    closeTimerRef.current = setTimeout(() => setActiveDropdown(null), 180);
  }
  function cancelClose() {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
  }

  function toggle() {
    if (sidebarExpanded) setActiveDropdown(null);
    toggleSidebarExpanded();
  }

  const state = isExpanded ? 'expanded' : 'collapsed';

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      {/* ── Outer fixed wrapper ── */}
      <div
        ref={wrapperRef}
        className="fixed left-3 top-3 bottom-3 z-50 hidden lg:block"
        onMouseEnter={() => { setShowToggle(true); setIsHovered(true); }}
        onMouseLeave={() => { setShowToggle(false); setIsHovered(false); }}
      >
        {/* ── Sidebar card — one persistent tree driven by a single variant state ── */}
        <motion.aside
          className={cn('relative h-full flex flex-col rounded-2xl overflow-hidden', CARD_BG)}
          variants={asideVariants}
          initial={false}
          animate={state}
        >
          {/* Brand */}
          <Link
            to="/dashboard"
            aria-label="Go to home"
            className="flex h-14 flex-shrink-0 items-center border-b border-white/[0.06] px-2"
          >
            <span className={ICON_COL}>
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-electric-500 to-neon-cyan shadow-lg">
                <svg className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l9-5-9-5-9 5 9 5z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0112 20.055a11.952 11.952 0 01-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                </svg>
              </span>
            </span>
            <motion.span
              variants={labelVariants}
              className="block shrink-0 overflow-hidden whitespace-nowrap text-sm font-semibold text-white"
            >
              Sci<span className="bg-gradient-to-r from-electric-400 to-neon-cyan bg-clip-text text-transparent">Campus</span>
            </motion.span>
          </Link>

          {/* Nav column */}
          <motion.nav
            variants={listVariants}
            className={cn(
              'flex-1 overflow-y-auto overflow-x-hidden px-2 py-3 space-y-1',
              // Subtle scrollbar that blends into the dark card (no plugin needed).
              '[scrollbar-width:thin] [scrollbar-color:rgba(255,255,255,0.14)_transparent]',
              '[&::-webkit-scrollbar]:w-1.5',
              '[&::-webkit-scrollbar-track]:bg-transparent',
              '[&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-white/10',
              '[&::-webkit-scrollbar-thumb:hover]:bg-white/20',
            )}
          >
            {/* Home */}
            <NavRow to="/dashboard" icon={<SvgIcon path={ICON_HOME} />} label="Home" />

            {/* AI Tools */}
            <GroupBlock
              icon={<SvgIcon path={ICON_AI} />}
              title="AI"
              active={onAiRoute}
              sidebarExpanded={isExpanded}
              open={!!openGroups.ai}
              onToggle={() => toggleGroup('ai')}
              onHoverOpen={(e) => openDropdown(e, 'ai')}
              onHoverClose={scheduleClose}
            >
              {AI_TOOLS_ITEMS.map((item) => <SubItemRow key={item.to} item={item} />)}
            </GroupBlock>

            {/* File Manager */}
            <NavRow to="/files" icon={<SvgIcon path={ICON_FILES} />} label="File Manager" />

            {/* Pricing */}
            <NavRow to={PRICING_ITEM.to} icon={PRICING_ITEM.icon} label={PRICING_ITEM.label} active={onPricingRoute} isLink />
          </motion.nav>

          {/* Footer: Settings + Profile */}
          <motion.div
            variants={listVariants}
            className="flex-shrink-0 border-t border-white/[0.06] px-2 py-2.5 space-y-0.5"
          >
            <NavRow to="/settings" icon={<Settings className="h-5 w-5" />} label="Settings" active={onSettingsRoute} isLink />

            <div className="flex items-center">
              <Link
                to="/profile"
                className="flex flex-1 items-center overflow-hidden rounded-xl py-2 pr-2 hover:bg-white/[0.06] transition-colors"
              >
                <span className={ICON_COL}>
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-600 text-xs font-semibold text-white shadow-sm">
                    {user.firstName[0]}{user.lastName[0]}
                  </span>
                </span>
                <motion.div variants={fadeVariants} className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-white">{user.fullName}</p>
                  <p className="truncate text-xs text-zinc-500">{isSuperAdmin ? 'Super Admin' : 'Member'}</p>
                </motion.div>
              </Link>
              {isExpanded && (
                <button
                  onClick={handleLogout}
                  title="Sign out"
                  className="flex-shrink-0 rounded-lg p-2 text-zinc-600 hover:text-red-400 hover:bg-white/[0.06] transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              )}
            </div>
          </motion.div>
        </motion.aside>

        {/* ── Toggle chevron (appears on hover, outside card) ── */}
        <AnimatePresence>
          {showToggle && !sidebarOpenOnHover && (
            <motion.button
              key="toggle"
              initial={{ opacity: 0, scale: 0.7 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.7 }}
              transition={{ duration: 0.15 }}
              onClick={toggle}
              title={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
              className={cn(
                'absolute top-[52px] right-0 translate-x-1/2 -translate-y-1/2',
                'flex h-6 w-6 items-center justify-center rounded-full',
                'bg-[#fff] border border-white/[0.14]',
                'text-[#000] shadow-lg transition-colors',
              )}
            >
              {isExpanded
                ? <ChevronLeft  className="h-4 w-4" />
                : <ChevronRight className="h-4 w-4" />}
            </motion.button>
          )}
        </AnimatePresence>

        {/* ── Hover dropdowns (collapsed state only, rendered outside overflow:hidden) ── */}
        <AnimatePresence>
          {!isExpanded && activeDropdown && (
            <motion.div
              key={activeDropdown}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -8 }}
              transition={{ duration: 0.15 }}
              style={{ top: dropdownY }}
              className={cn(
                'absolute left-[72px] min-w-[220px] rounded-2xl overflow-hidden',
                CARD_BG,
              )}
              onMouseEnter={cancelClose}
              onMouseLeave={scheduleClose}
            >
              {/* AI Tools dropdown */}
              {activeDropdown === 'ai' && (
                <div className="py-1.5">
                  <p className="px-4 pt-2 pb-2 text-[10px] font-semibold uppercase tracking-widest text-zinc-500">
                    AI Tools
                  </p>
                  {AI_TOOLS_ITEMS.map((item) => (
                    <DropdownLink key={item.to} item={item} onClick={() => setActiveDropdown(null)} />
                  ))}
                  <div className="h-1.5" />
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

      </div>
    </>
  );
}
